import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import * as itemReducers from '../../../reducers/cartSlice.itemReducers';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { selectRestaurant } from '../../../reducers/restaurantSlice';
import logger from '../../../utils/logger';
import {
  resetSBOrderStates,
  resetSessionBoundaryData,
  setSessionStart,
} from '../sessionBoundary/sessionBoundary.slice';
import {
  freshDraftCart,
  IDraftCartState,
  initialDraftCartState,
} from './draftCart.props';

const SLICE_NAME = 'draftCart';

/**
 * HITL-1619 — a new line opens; the lines already there stay as the agent left
 * them.
 *
 * Both `addItemToCart` and `cloneCartItem` end in `expandCollapseCartItems`,
 * which folds every previously auto-expanded line shut so the newest is the only
 * one open. That is right for `cartSlice`, and in a live order it is also INERT
 * there: `shouldCollapse` (`utils/cart.ts:871`) additionally requires an order
 * `status`, so nothing in the AI's column is ever auto-collapsed in front of the
 * agent.
 *
 * A draft is never sent and so has no status — `DraftCartItemReview` says
 * exactly that — which left the cascade running unopposed in the one cart with
 * no counterweight to it. Copy a line in and its modifiers show; copy a second
 * and the first silently folds, which reads as the copy having dropped them.
 *
 * The two carts sit side by side showing the same items, so they collapse by the
 * same rule: only when the agent says so. Wrapped here rather than changed in
 * `cartSlice.itemReducers` — that function is shared, and `cartSlice`'s
 * post-send behaviour is not this ticket's to move.
 *
 * The snapshot holds only ids that were present BEFORE the write, so a line the
 * reducer has just created keeps whatever the reducer decided for it
 * (AUTO_EXPAND). The one case this would get wrong is a write that re-uses an id
 * already in the cart; no caller does that — every one of them mints from
 * `sequenceId`.
 */
const keepingExistingLinesOpen =
  <A>(apply: (state: IDraftCartState, action: A) => void) =>
  (state: IDraftCartState, action: A) => {
    const before = { ...state.cartItemCollapseStates };
    apply(state, action);
    Object.entries(before).forEach(([cartItemId, collapseState]) => {
      state.cartItemCollapseStates[Number(cartItemId)] = collapseState;
    });
  };

/**
 * HITL-1611 — a second cart store the AI's rebuild cannot reach.
 *
 * `useHypothesisFrames` dispatches `clearCart()` and rebuilds `cartSlice` from
 * scratch on every changed hypothesis frame, and `clearCart` returns
 * `initialCartState`. Anything the agent builds there while the AI is driving is
 * destroyed mid-order. This slice is where their work lives instead, which is
 * why the rest of Phase C cannot start anywhere else.
 *
 * The shared item reducers are the SAME functions `cartSlice` uses, imported from
 * `cartSlice.itemReducers` rather than copied, so the two carts cannot drift as
 * the cart evolves. RTK still mints separate action creators per slice
 * (`draftCart/addItemToCart` vs `cart/addItemToCart`), which is precisely the
 * distinction C2's agent-origin guard needs.
 *
 * C1 was foundation only — nothing dispatched here and nothing rendered it.
 * HITL-1614 gave it a column and a copy control, HITL-1615 a way out into the
 * order, and HITL-1617 routes the centre pane's writes here, which is when
 * `applyOptionReplacement` had to join the shared set.
 */
export const draftCartSlice = createSlice({
  name: SLICE_NAME,
  initialState: initialDraftCartState,
  reducers: {
    addItemToCart: keepingExistingLinesOpen(itemReducers.addItemToCart),
    // HITL-1617: item conversion's second half. A combo-size pick on a My Cart
    // line re-picks the dependent side and drink sizes through this, and it has
    // to land in the draft — on `cartSlice` alone it would rewrite the AI's item.
    applyOptionReplacement: itemReducers.applyOptionReplacement,
    cloneCartItem: keepingExistingLinesOpen(itemReducers.cloneCartItem),
    deleteCartItem: itemReducers.deleteCartItem,
    selectModifier: itemReducers.selectModifier,
    selectModifiers: itemReducers.selectModifiers,
    setCartItemCollapseState: itemReducers.setCartItemCollapseState,
    setExplicitExpandState: itemReducers.setExplicitExpandState,
    updateQuantity: itemReducers.updateQuantity,
    /** Draft-only. `cartSlice.clearCart` is a different action and never reaches here. */
    clearDraftCart: () => freshDraftCart(),
  },
  extraReducers: (builder) => {
    // Session start, path 1 of 2 — the task-router flow.
    builder.addCase(setSessionStart, () => freshDraftCart());

    // Restaurant change and the task-router WS teardown; both between orders.
    builder.addCase(resetSessionBoundaryData, () => freshDraftCart());

    // A draft holds CartItems built against one menu version. A restaurant or
    // version change leaves it holding items that no longer exist, so it clears
    // on both — `dialogSlice` resets on the same two for the same reason. This
    // is the one place the draft needs more than `agentMode`, which holds only
    // an enum.
    builder.addCase(selectRestaurant.fulfilled, () => freshDraftCart());
    builder.addCase(selectMenuVersion.fulfilled, () => freshDraftCart());

    /**
     * HITL-1619 — the order is over.
     *
     * `OrderActions.resetStatesAfterFinalOrderSubmit` dispatches this in the
     * SAME chain as `cartActions.clearCart()`, once per completed or cancelled
     * order. Without it the AI cart emptied and My Cart did not: the agent's
     * shadow of a finished order survived until the next car arrived, and in
     * the single-restaurant flow that window is unbounded.
     *
     * Keyed on this and deliberately NOT on `cartActions.clearCart()`, which is
     * the obvious hook and is a trap — `useHypothesisFrames` dispatches
     * `clearCart()` on every changed hypothesis frame, which is the exact event
     * this slice exists to survive. Listening to it would destroy the agent's
     * cart several times per conversation.
     *
     * Not `endSession` / `car_exit` either, for the reason given below: a
     * takeover lets the car leave while the same order is still being finished.
     * `resetSBOrderStates` is the narrower signal — not "the car went" but "the
     * order is done".
     */
    builder.addCase(resetSBOrderStates, (state) => {
      const unpromoted = Object.keys(state.cartItems).length;
      if (unpromoted) {
        logger.info(
          `Draft cart cleared on order completion (${unpromoted} unpromoted item(s))`
        );
      }
      return freshDraftCart();
    });

    // Session start, path 2 of 2 — the single-restaurant flow, where a session
    // begins on car_enter.
    //
    // Deliberately NOT `car_exit`, and not session end. A takeover lets the car
    // leave while the same session's order is still being finished; clearing the
    // agent's control state there is exactly what caused the dropped-order and
    // cart-revert incident, and a draft they are still working from is the same
    // kind of state. `agentMode` carries the full reasoning.
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const {
          messages = [],
          isSessionBufferedMessages,
          isCartEmpty,
        } = action.payload;

        // A buffered payload REPLAYS past events, so its car_enter is history
        // rather than a new customer.
        if (isSessionBufferedMessages) return;

        // The previous order is still in flight (the car left, the agent is
        // finishing it) — leave the draft alone. The main cart is the durable
        // signal, same as in `agentMode`.
        if (!isCartEmpty) return;

        if (messages.some(({ event }) => event === EventTypes.carEnter)) {
          if (Object.keys(state.cartItems).length) {
            logger.info(
              `Draft cart cleared on car_enter (${
                Object.keys(state.cartItems).length
              } unpromoted item(s))`
            );
          }
          return freshDraftCart();
        }
      }
    );
  },
});

export const draftCartActions = draftCartSlice.actions;
export const { clearDraftCart } = draftCartActions;

export default draftCartSlice.reducer;
